import { useEffect } from "react";
import { withRouter } from "react-router-dom";
import { applyRoute } from "./dockStore";

/**
 * Maps the current route to { slot, mode } and updates dockStateStore so
 * <VisualizerDockShell/> knows which slot to overlay and at what dock mode.
 *
 * Driven primarily by react-router-dom's own `location` (via withRouter), NOT
 * the native `hashchange` event. That matters: `history.push()` (e.g. from
 * shoppingCartButton.js) updates react-router's location — and therefore
 * unmounts/mounts the page-level <DockSlot/>s via <Switch/> — synchronously,
 * well before the native `hashchange` event fires. Relying on `hashchange`
 * alone left a one-tick window where the old page's <DockSlot/> had already
 * unregistered but dockStateStore still pointed at it (and not yet at the new
 * page's slot), so <VisualizerDockShell/> hid itself for a frame — read as
 * DockedVisualizerWrapper "remounting"/blinking. Browser back/forward didn't
 * show this because those are driven purely by the native hashchange event,
 * so react-router's location update and the old hashchange-only sync always
 * landed in the same tick.
 *
 * The hashchange listener is kept as a fallback for hash changes that don't
 * go through this Router's own history (e.g. the legacy react-router v3
 * `hashHistory` singleton still used in a couple of places).
 */
function DockRouteSync({ location }) {
  useEffect(() => {
    applyRoute(location.pathname || "/");
  }, [location.pathname]);

  useEffect(() => {
    const sync = () => {
      // hash is like "#/visualizer" or "#/newscope?foo=bar"
      const raw = window.location.hash.replace(/^#/, "") || "/";
      const pathname = raw.split("?")[0] || "/";
      applyRoute(pathname);
    };

    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  return null;
}

export default withRouter(DockRouteSync);
